import React, { Component } from 'react'
import { extendObservable } from 'mobx'
import { observer, inject } from 'mobx-react'
import PropTypes from 'prop-types'
import { Alert } from 'antd'
import t from '../../service/translate.service'
import ContractSelector from '../../components/contractSelector'
import { Form } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import historyService from '../../service/history.service'
import pdfIcon from '../../assets/pdf-img.png'
import ThankYouOverlay from '../../components/ThankYouOverlay'
import LoadingPageOverlay from '../../components/LoadingPageOverlay'
import withRouter from '../../components/withRouter'
import { WarningIcon } from '../../components/icons/warningIcon'
import './RepairsAndInquiriesNewRequestV2.css'

// Import PNG icons for service request categories (damage report flow)
import dmPlumbingIcon from '../../assets/dm_plumbing_icon.png'
import dmElectricalIcon from '../../assets/dm_electrical_icon.png'
import dmHeatingIcon from '../../assets/dm_heating_icon.png'
import dmApplianceIcon from '../../assets/dm_appliance_icon.png'
import dmOtherIcon from '../../assets/dm_other_icon.png'
import { extractError, extractErrorFromSap } from '../../service/response.service'
import resizer from 'react-image-file-resizer'
import ContactDetailsModal from '../../components/ContactDetailsModal'
import { appFeatureEnabled } from '../../service/appFeatures.service'
import { Input, Textarea, Checkbox } from '../../components/formComponents'

class RepairsAndInquiriesNewRequestV2Class extends Component {
  static contextTypes = {
    user: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props)

    extendObservable(this, {
      currentStep: 1,
      selectedContract: null,
      requestType: null, // 'general' or 'damage_report'
      selectedCategory: null,
      selectedRooms: [], // Changed to array for multiple selection
      selectedUrgency: null,
      affectsNeighbors: null,
      description: '',
      files: [],
      removedFileIds: [], // Track IDs of removed existing files
      rooms: [],
      loadingRooms: false,
      roomsLoadedForContract: null, // Track which contract's rooms are loaded
      roomsLoadError: null, // Error when loading rooms fails
      roomSelectionsByContract: {}, // Store room selections per contract
      submitting: false, // Added for submit loading state
      showThankYou: false, // Added for thank you overlay
      error: null, // Added for error handling
      isEditMode: false, // Flag to indicate if we're editing
      serviceRequestId: null, // ID of the service request being edited
      loadingServiceRequest: false, // Loading state for fetching service request
      loadingContractStep: false, // Loading state when fetching contract details/type
      contractType: null, // Contract type from API: { typeOfUsageMain, descriptionM, descriptionS }
      analyzingImages: new Set(), // Track which images are being analyzed
      imageAnalysisResults: {}, // Store analysis results by file ID
      showTooltipFor: null, // Track which file's tooltip is shown (for click functionality)
      lastAnalysisCategory: null, // Track category used for last analysis
      lastAnalysisDescription: null, // Track description used for last analysis
      hasAnalyzedOnFileStep: false, // Track if we've analyzed images when landing on file step
      stepAnnouncement: '', // For screen reader announcements when step changes
      fileActionAnnouncement: '', // For file action announcements
      previousLoadingState: false, // Track previous loading state for announcements
      subject: '', // Subject for general request
      categories: [
        { id: 1, name: t['SR_STEP_CATEGORY_TITLE_PLUMBING'], icon: dmPlumbingIcon, description: t['SR_STEP_CATEGORY_DESC_PLUMBING'] },
        { id: 2, name: t['SR_STEP_CATEGORY_TITLE_ELECTRICAL'], icon: dmElectricalIcon, description: t['SR_STEP_CATEGORY_DESC_ELECTRICAL'] },
        { id: 3, name: t['SR_STEP_CATEGORY_TITLE_HEATING'], icon: dmHeatingIcon, description: t['SR_STEP_CATEGORY_DESC_HEATING'] },
        { id: 4, name: t['SR_STEP_CATEGORY_TITLE_APPLIANCE'], icon: dmApplianceIcon, description: t['SR_STEP_CATEGORY_DESC_APPLIANCE'] },
        { id: 5, name: t['SR_STEP_CATEGORY_TITLE_OTHER'], icon: dmOtherIcon, description: t['SR_STEP_CATEGORY_DESC_OTHER'] }
      ],
      urgencies: [
        {
          id: 1,
          name: t['SR_STEP_URGENCY_TITLE_EMERGENCY'],
          description: t['SR_STEP_URGENCY_DESC_EMERGENCY'],
          level: 'emergency',
          color: '#dc3545'
        },
        {
          id: 2,
          name: t['SR_STEP_URGENCY_TITLE_HIGH'],
          description: t['SR_STEP_URGENCY_DESC_HIGH'],
          level: 'high',
          color: '#fd7e14'
        },
        {
          id: 3,
          name: t['SR_STEP_URGENCY_TITLE_MEDIUM'],
          description: t['SR_STEP_URGENCY_DESC_MEDIUM'],
          level: 'medium',
          color: '#ffc107'
        },
        {
          id: 4,
          name: t['SR_STEP_URGENCY_TITLE_LOW'],
          description: t['SR_STEP_URGENCY_DESC_LOW'],
          level: 'low',
          color: '#28a745'
        }
      ],
      neighborOptions: [
        { id: 'yes', label: t['SR_STEP_NEIGHBOR_OPTION_YES'], value: true },
        { id: 'no', label: t['SR_STEP_NEIGHBOR_OPTION_NO'], value: false }
      ]
    })
  }

  componentDidMount = async () => {
    window.scrollTo(0, 0)
    
    // Check if we're in edit mode by looking for serviceRequestId in URL params
    const serviceRequestId = this.props.useParams?.serviceRequestId
    if (serviceRequestId) {
      this.isEditMode = true
      this.serviceRequestId = serviceRequestId
      this.loadServiceRequestData(serviceRequestId)
    } else {
      await this.initializeContractSelection()
      // Announce initial step after component mounts (for new request)
      setTimeout(() => {
        this.announceCurrentStep()
      }, 300)
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    // Announce step when it changes (for edit mode after loading)
    // Check if loading just finished
    if (this.previousLoadingState && !this.loadingServiceRequest && this.isEditMode) {
      setTimeout(() => {
        this.announceCurrentStep()
      }, 300)
    }
    
    // Update previous loading state
    this.previousLoadingState = this.loadingServiceRequest
  }

  announceCurrentStep = () => {
    const contactDetailsEnabled = appFeatureEnabled('contactDetailsEnabled')
    const stepTitles = [
      '',
      t['SR_STEP_CONTRACT_TITLE'],
      ...(contactDetailsEnabled ? [t['SR_STEP_TITLE_CONTACT_DETAILS'] || t.CONTACT_DETAILS_TITLE] : []),
      t['SR_STEP_REQUEST_TYPE_TITLE'] || 'Request Type',
      t['SR_STEP_GENERAL_REQUEST_TITLE'] || 'General Request',
      t['SR_STEP_CATEGORY_TITLE'],
      t['SR_STEP_ROOM_TITLE'],
      t['SR_STEP_URGENCY_TITLE'],
      t['SR_STEP_NEIGHBOR_TITLE'],
      t['SR_STEP_DESCRIPTION_TITLE'],
      t['SR_STEP_FILE_TITLE'],
      t['SR_STEP_REVIEW_TITLE']
    ]
    this.stepAnnouncement = `${t['SR_STEP_CHANGED'] || 'Step'} ${this.currentStep}: ${stepTitles[this.currentStep] || ''}`
    setTimeout(() => {
      this.stepAnnouncement = ''
    }, 1000)
  }

  initializeContractSelection = async () => {
    const { activeContracts } = this.props.contractStore
    
    // If user has only one contract, preselect it and skip to request type selection
    if (activeContracts.length === 1) {
      this.selectedContract = activeContracts[0]
      this.props.contractStore.selectedContract = activeContracts[0]
      this.currentStep = 2
      // Fetch contract type when contract is auto-selected
      if (this.selectedContract?.contractNumber) {
        this.loadingContractStep = true
        try {
          this.contractType = await this.props.contractStore.getContractType(this.selectedContract.contractNumber)
        } catch (err) {
          // Non-blocking: allow user to continue even if contract type fetch fails
        } finally {
          this.loadingContractStep = false
        }
      }
    }
  }

  get showRoomStep() {
    return this.requestType === 'damage_report' && this.contractType?.typeOfUsageMain === '0000'
  }

  loadServiceRequestData = async (serviceRequestId) => {
    try {
      this.loadingServiceRequest = true
      this.previousLoadingState = true
      
      // Fetch service request data from API
      const serviceRequest = await this.props.repairsAndIquiriesStore.getTicketById(serviceRequestId)
      
      if (serviceRequest) {
        // Prepopulate form with service request data (including files)
        await this.populateFormWithServiceRequest(serviceRequest)
      }
      
      this.loadingServiceRequest = false
      // componentDidUpdate will handle the announcement
    } catch (error) {
      this.loadingServiceRequest = false
      this.previousLoadingState = false
      // Handle error - maybe redirect back to list or show error message
    }
  }

  populateFormWithServiceRequest = async (serviceRequest) => {
    const rooms = await this.props.repairsAndIquiriesStore.getPropertyRooms(serviceRequest.contract_number)

    // Set contract
    if (serviceRequest.contract) {
      this.selectedContract = serviceRequest.contract
      this.props.contractStore.selectedContract = serviceRequest.contract
    }
    // Parse description JSON once and use for both category and description
    let parsedDescription = null
    if (serviceRequest.description) {
      try {
        parsedDescription = JSON.parse(serviceRequest.description)
      } catch (e) {
      }
    }

    // Set category based on parsed description JSON
    if (parsedDescription && parsedDescription.categoryId) {
      // Use ID for matching (more reliable than name)
      const category = this.categories.find(cat => cat.id === parsedDescription.categoryId)
      if (category) {
        this.selectedCategory = category
      } else {
      }
    } else if (parsedDescription && parsedDescription.category) {
      // Fallback to name matching for backward compatibility
      const category = this.categories.find(cat => cat.name === parsedDescription.category)
      if (category) {
        this.selectedCategory = category
      } else {
      }
    }

    // Set urgency based on parsed description JSON or urgent flag
    if (parsedDescription && parsedDescription.urgencyId) {
      // Use ID for matching (more reliable than name)
      const urgency = this.urgencies.find(urg => urg.id === parsedDescription.urgencyId)
      if (urgency) {
        this.selectedUrgency = urgency
      } else {
      }
    } else if (parsedDescription && parsedDescription.urgency) {
      // Fallback to name matching for backward compatibility
      const urgency = this.urgencies.find(urg => urg.name === parsedDescription.urgency)
      if (urgency) {
        this.selectedUrgency = urgency
      } else {
      }
    } else if (serviceRequest.urgent === true || serviceRequest.urgency === "Urgent") {
      // Fallback to urgent flag if no JSON urgency
      this.selectedUrgency = this.urgencies.find(urg => urg.level === 'high') || this.urgencies[1]
    } else if (serviceRequest.urgency === "Normal") {
      this.selectedUrgency = this.urgencies.find(urg => urg.level === 'low') || this.urgencies[3]
    }

    // Set description text
    if (serviceRequest.description) {
      if (parsedDescription) {
        // Check if description exists in parsed JSON (even if empty string)
        if (parsedDescription.hasOwnProperty('description')) {
          this.description = parsedDescription.description || ''
        } else if (parsedDescription.hasOwnProperty('text')) {
          this.description = parsedDescription.text || ''
        } else {
          this.description = ''
        }
      } else {
        this.description = serviceRequest.description
      }
    }

    // Set rooms based on parsed description JSON
    if (parsedDescription && parsedDescription.rooms && Array.isArray(parsedDescription.rooms)) {
      // Rooms are stored as array of objects with name, id, and description
      const selectedRooms = []
      parsedDescription?.rooms?.forEach((room) => {
        const foundRoom = rooms.find((r) => r.id == room.id)
        if (foundRoom) {
          selectedRooms.push(foundRoom)
        }
      })
      this.selectedRooms = selectedRooms
    } else if (serviceRequest.rooms) {
      // Fallback to serviceRequest.rooms if no JSON rooms
      try {
        const rooms = typeof serviceRequest.rooms === 'string' ? JSON.parse(serviceRequest.rooms) : serviceRequest.rooms
        if (Array.isArray(rooms) && rooms.length > 0) {
          this.selectedRooms = rooms
        }
      } catch (e) {
      }
    }

    // Set affects neighbors based on parsed description JSON
    if (parsedDescription && parsedDescription.affectsNeighbors !== undefined) {
      this.affectsNeighbors = parsedDescription.affectsNeighbors
    } else if (serviceRequest.affectsNeighbors !== undefined) {
      // Fallback to serviceRequest.affectsNeighbors if no JSON value
      try {
        const neighbors = typeof serviceRequest.affectsNeighbors === 'string' ? JSON.parse(serviceRequest.affectsNeighbors) : serviceRequest.affectsNeighbors
        this.affectsNeighbors = neighbors
      } catch (e) {
        this.affectsNeighbors = serviceRequest.affectsNeighbors
      }
    }

    // Load rooms for the contract
    if (this.selectedContract) {
      try {
        this.loadRoomsForContract(this.selectedContract.contractNumber)
      } catch (error) {
      }
    } else {
    }

    // Load existing files/attachments
    try {

      
      if (serviceRequest.code) {
        try {
          const attachments = await this.props.repairsAndIquiriesStore.getAttachments(serviceRequest.code)
          if (attachments && attachments.length > 0) {
            // Convert attachments to file objects that the form can handle
            this.files = await Promise.all(attachments.map(async (attachment) => {
              
              // Convert base64 data to blob URL for display
              let previewUrl = null
              if (attachment.att_datas) {
                try {
                  // Convert base64 to blob
                  const byteCharacters = atob(attachment.att_datas)
                  const byteNumbers = new Array(byteCharacters.length)
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i)
                  }
                  const byteArray = new Uint8Array(byteNumbers)
                  
                  // Determine MIME type from file extension
                  const fileExtension = attachment.file_name.split('.').pop().toLowerCase()
                  let mimeType = 'application/octet-stream'
                  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
                    mimeType = `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`
                  } else if (fileExtension === 'pdf') {
                    mimeType = 'application/pdf'
                  }
                  
                  const blob = new Blob([byteArray], { type: mimeType })
                  previewUrl = URL.createObjectURL(blob)
                  
                } catch (error) {
                }
              }
              
              return {
                name: attachment.file_name,
                size: attachment.att_datas ? Math.round(attachment.att_datas.length * 0.75) : 0, // Approximate size from base64
                type: attachment.mimetype || this.getMimeTypeFromFileName(attachment.file_name),
                url: previewUrl,
                preview: previewUrl, // For image display
                isExisting: true, // Flag to indicate this is an existing file
                attachmentId: attachment.id || attachment.attachment_id,
                id: attachment.id || attachment.attachment_id || Date.now() + Math.random(),
                base64Data: attachment.att_datas, // Keep original data for potential re-upload
                imageReference: (attachment.mimetype || this.getMimeTypeFromFileName(attachment.file_name)).startsWith('image/') ? `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null // Generate reference for images
              }
            }))
          } else {
          }
        } catch (error) {
          // Don't fail the entire form population if attachments fail to load
        }
      } else {
      }
    } catch (error) {
    }

    // Set request type to 'damage_report' for edit mode (existing requests are damage reports)
    this.requestType = 'damage_report'

    // Fetch contract type for room step visibility (typeOfUsageMain === '0000' shows room step)
    if (this.selectedContract?.contractNumber) {
      try {
        this.contractType = await this.props.contractStore.getContractType(this.selectedContract.contractNumber)
      } catch (err) {
        // Non-blocking: allow edit to continue even if contract type fetch fails
      }
    }
    
    // Skip to step 2 (request type selection) only if user has only one contract
    // If user has multiple contracts, start from step 1 to allow contract selection
    const { activeContracts } = this.props.contractStore
    if (activeContracts.length === 1) {
      this.currentStep = 2
    } else {
      this.currentStep = 1
    }
  }

  getMimeTypeFromFileName = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase()
    const mimeTypes = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'pdf': 'application/pdf',
      // svg intentionally not allowed
    }
    return mimeTypes[extension] || 'application/octet-stream'
  }

  handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      action()
    }
  }

  handleContractSelect = (contract) => {
    const previousContract = this.selectedContract
    
    // Save current room selections for the previous contract
    if (previousContract && this.selectedRooms.length > 0) {
      this.roomSelectionsByContract[previousContract.contractNumber] = [...this.selectedRooms]
    }
    
    this.selectedContract = contract
    this.props.contractStore.selectedContract = contract
    
    // Only clear selected rooms if contract actually changed
    if (previousContract && previousContract.contractNumber !== contract.contractNumber) {
      this.rooms = []
      this.roomsLoadedForContract = null // Reset the tracking variable
      this.roomsLoadError = null // Clear error when switching contracts
      
      // Restore room selections for the new contract if they exist
      if (this.roomSelectionsByContract[contract.contractNumber]) {
        this.selectedRooms = [...this.roomSelectionsByContract[contract.contractNumber]]
      } else {
        this.selectedRooms = []
      }
    }
  }

  handleRequestTypeSelect = (requestType) => {
    this.requestType = requestType
    this.selectedCategory = null // Clear so general/damage categories don't leak across
    // Don't auto-navigate - wait for next button click
  }

  handleCategorySelect = (category) => {
    this.selectedCategory = category
  }

  handleRoomSelect = (room) => {
    const roomIndex = this.selectedRooms.findIndex(r => r.id === room.id)
    if (roomIndex > -1) {
      // Room already selected, remove it
      this.selectedRooms.splice(roomIndex, 1)
    } else {
      // Room not selected, add it
      this.selectedRooms.push(room)
    }
    
    // Save the updated room selections for the current contract
    if (this.selectedContract) {
      this.roomSelectionsByContract[this.selectedContract.contractNumber] = [...this.selectedRooms]
    }
  }

  handleUrgencySelect = (urgency) => {
    this.selectedUrgency = urgency
  }

  handleNeighborSelect = (option) => {
    this.affectsNeighbors = option.value
  }

  handleDescriptionChange = (event) => {
    this.description = event.target.value
  }

  handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files)
    await this.addFiles(selectedFiles)
    // Reset the input value to allow selecting the same file again
    if (event.target) {
      event.target.value = ''
    }
  }

  handleFileDrop = async (event) => {
    event.preventDefault()
    const droppedFiles = Array.from(event.dataTransfer.files)
    await this.addFiles(droppedFiles)
  }

  handleDragOver = (event) => {
    event.preventDefault()
  }

  resizeImage = (file) => {
    return new Promise((resolve) => {
      // Only resize JPEG, JPG, and PNG images (not PDF or SVG)
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png']
      
      if (!imageTypes.includes(file.type)) {
        // Return original file if it's PDF or SVG
        resolve(file)
        return
      }

      try {
        resizer.imageFileResizer(
          file,
          1600,
          1600,
          'jpeg',
          90,
          0,
          (resizedFile) => {
            resolve(resizedFile)
          },
          'file'
        )
      } catch (error) {
        // If resizing fails, return original file
        resolve(file)
      }
    })
  }

  analyzeImage = async (fileId, file, imageReference) => {
    
    // Only analyze images, not PDFs
    if (!file.type.startsWith('image/')) {
      return
    }

    // Mark as analyzing
    this.analyzingImages.add(fileId)

    try {
      const userLang = localStorage.getItem("locale") === "en" ? "EN" : "DE"
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', this.selectedCategory?.name || '')
      formData.append('description', this.description || '')
      formData.append('imageReference', imageReference)
      formData.append('lang', userLang)

      const result = await this.props.repairsAndIquiriesStore.imageAnalyze(formData)
      
      // Store the analysis result
      this.imageAnalysisResults[fileId] = result
      
      // Track the category and description used for this analysis
      this.lastAnalysisCategory = this.selectedCategory?.name || ''
      this.lastAnalysisDescription = this.description || ''
      
      // Announce analysis error if match is false
      if (result && result.match === false && result.reason) {
        const fileName = this.files.find(f => f.id === fileId)?.name || 'image'
        this.fileActionAnnouncement = `${t['SR_STEP_FILE_ANALYSIS_ERROR'] || 'Image analysis error'}: ${fileName}. ${result.reason}`
        // Clear announcement after a delay
        setTimeout(() => {
          if (this.fileActionAnnouncement.includes('analysis error')) {
            this.fileActionAnnouncement = ''
          }
        }, 5000)
      }
    } catch (error) {
      // Store error result
      const fileName = this.files.find(f => f.id === fileId)?.name || 'image'
      this.imageAnalysisResults[fileId] = {
        match: false,
        reason: t['SR_STEP_FILE_ANALYSIS_FAILED'] || 'Analysis failed'
      }
      // Announce analysis error
      this.fileActionAnnouncement = `${t['SR_STEP_FILE_ANALYSIS_ERROR'] || 'Image analysis error'}: ${fileName}. ${t['SR_STEP_FILE_ANALYSIS_FAILED'] || 'Analysis failed'}`
      // Clear announcement after a delay
      setTimeout(() => {
        if (this.fileActionAnnouncement.includes('analysis error')) {
          this.fileActionAnnouncement = ''
        }
      }, 5000)
    } finally {
      // Remove from analyzing set
      this.analyzingImages.delete(fileId)
    }
  }

  reanalyzeAllImages = async () => {
    // Get all image files that need re-analysis
    const imageFiles = this.files.filter(fileItem => 
      fileItem.type.startsWith('image/') && fileItem.imageReference
    )

    // Re-analyze each image
    for (const fileItem of imageFiles) {
      await this.analyzeImage(fileItem.id, fileItem.file, fileItem.imageReference)
    }
  }

      addFiles = async (newFiles) => {
        const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB in bytes
        const MAX_FILES = 10
        
        // Filter files by type and size, and respect max files limit
        const validFiles = newFiles.filter(file => {
          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
          return validTypes.includes(file.type) && file.size <= MAX_FILE_SIZE
        })
        
        // Only take files up to the max limit
        const currentFileCount = this.files.length
        const remainingSlots = Math.max(0, MAX_FILES - currentFileCount)
        const filesToAdd = validFiles.slice(0, remainingSlots)
    
    for (const file of filesToAdd) {
      // Resize image if it's not a PDF
      const processedFile = await this.resizeImage(file)
      const preview = await this.createPreview(processedFile)
      const fileId = Date.now() + Math.random()
      // Generate unique image reference for Redis storage
      const imageReference = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const fileWithPreview = {
        id: fileId,
        file: processedFile,
        name: file.name, // Keep original name
        size: processedFile.size,
        type: processedFile.type || file.type, // Resized images will be JPEG
        preview: preview,
        isExisting: false, // Mark new files as not existing
        imageReference: imageReference
      }
      this.files.push(fileWithPreview)
      
      // Analyze image if it's an image type
      if (processedFile.type.startsWith('image/')) {
        // Only analyze if not already analyzed
        if (!this.imageAnalysisResults[fileId]) {
          this.analyzeImage(fileId, processedFile, imageReference)
        }
      }
    }
  }

  createPreview = (file) => {
    return new Promise((resolve) => {
      if (file && file.type === 'image/svg+xml') {
        // Use object URL for SVG for better compatibility
        const url = URL.createObjectURL(file)
        resolve(url)
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.readAsDataURL(file)
    })
  }

  removeFile = (fileId) => {
    // Find the file being removed
    const fileToRemove = this.files.find(file => file.id === fileId)
    
    // Announce file removal for screen readers
    if (fileToRemove) {
      this.fileActionAnnouncement = `${t['SR_STEP_FILE_REMOVED'] || 'File removed'}: ${fileToRemove.name}`
    }
    
    // If it's an existing file, track its ID for deletion
    if (fileToRemove && fileToRemove.isExisting && fileToRemove.attachmentId) {
      this.removedFileIds.push(fileToRemove.attachmentId)
    }
    
    // Clean up analysis data
    this.analyzingImages.delete(fileId)
    delete this.imageAnalysisResults[fileId]

    // Revoke object URL previews to avoid leaks
    if (fileToRemove && typeof fileToRemove.preview === 'string' && fileToRemove.preview.startsWith('blob:')) {
      try { URL.revokeObjectURL(fileToRemove.preview) } catch (e) { /* ignore */ }
    }
    
    // Remove the file from the files array
    this.files = this.files.filter(file => file.id !== fileId)
    
    // Clear announcement after a short delay
    setTimeout(() => {
      this.fileActionAnnouncement = ''
    }, 2000)
  }

  formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  handleSubmit = async () => {
    this.submitting = true
    this.error = null // Clear any previous errors
    try {
      const userLang = localStorage.getItem("locale") === "en" ? "EN" : "DE"
      const formData = new FormData()
      
      // Handle general request
      if (this.requestType === 'general') {
        const requestData = {
          requestType: 'general',
          contractNumber: this.selectedContract?.contractNumber,
          subject: this.subject,
          description: this.description,
          urgency: this.selectedUrgency?.name || t['SR_STEP_URGENCY_TITLE_LOW'],
          urgencyId: this.selectedUrgency?.id || 4
        }

        formData.append('subject', this.subject)
        formData.append('description', JSON.stringify(requestData))
        formData.append('contractNumber', this.selectedContract?.contractNumber)
        formData.append('requestType', 'general')
        formData.append('urgent', this.selectedUrgency?.level === 'high' || this.selectedUrgency?.level === 'emergency')
        formData.append('lang', userLang)
        
        // Add files if any (general request uses same shape as damage report: { id, file, name, ... })
        this.files.forEach((fileItem, index) => {
          const file = fileItem.file != null ? fileItem.file : fileItem
          formData.append(`file_${index}`, file)
        })
      } else {
        // Handle damage report
        const requestData = {
          requestType: this.requestType,
          contractNumber: this.selectedContract?.contractNumber,
          category: this.selectedCategory?.name,
          categoryId: this.selectedCategory?.id,
          rooms: this.selectedRooms.map(room => ({
            name: room.name,
            nameDe: room.nameDe,
            id: room.id
          })),
          urgency: this.selectedUrgency?.name,
          urgencyId: this.selectedUrgency?.id,
          affectsNeighbors: this.affectsNeighbors,
          description: this.description
        }

        formData.append('subject', `${this.selectedCategory?.name || 'Request'} - ${this.selectedContract?.contractNumber}`)
        formData.append('description', JSON.stringify(requestData))
        formData.append('contractNumber', this.selectedContract?.contractNumber)
        formData.append('requestType', this.requestType || 'damage_report')
        formData.append('urgent', this.selectedUrgency?.level === 'high' || this.selectedUrgency?.level === 'emergency')
        formData.append('lang', userLang)
        
        // For edit mode, filter out existing files before adding to FormData
        const filesToProcess = this.isEditMode ? this.files.filter(file => !file.isExisting) : this.files
        
        // Collect ALL image references (both new and existing images)
        const imageReferences = this.files
          .filter(fileItem => fileItem.imageReference !== null && fileItem.imageReference !== undefined)
          .map(fileItem => fileItem.imageReference)
        
        // Add image references to FormData (all references, not just new files)
        if (imageReferences.length > 0) {
          formData.append('imageReferences', JSON.stringify(imageReferences))
        }
        
        // Add files if any (only new files in edit mode)
        filesToProcess.forEach((fileItem, index) => {
          formData.append(`file_${index}`, fileItem.file)
        })

        // For edit mode, add removed file IDs
        if (this.isEditMode && this.removedFileIds.length > 0) {
          formData.append('removedFileIds', this.removedFileIds)
        }
      }

      // Call the appropriate store method based on mode
      if (this.isEditMode && this.serviceRequestId) {
        await this.props.repairsAndIquiriesStore.updateDamageReport(this.serviceRequestId, formData)
      } else {
        await this.props.repairsAndIquiriesStore.createDamageReportV2(formData)
      }
      
      // Success - show thank you overlay
      this.showThankYou = true
      
    } catch (error) {
      this.error = extractErrorFromSap(error) || t[extractError(error)] || t['ERROR']
    } finally {
      this.submitting = false
    }
  }

  loadRooms = async () => {
    if (!this.selectedContract) {
      return
    }
    
    this.loadingRooms = true
    this.roomsLoadError = null // Clear previous errors
    // Announce loading for screen readers
    this.stepAnnouncement = t['SR_STEP_ROOM_LOADING_ROOMS'] || 'Loading rooms...'
    
    try {
      const contractNumber = this.selectedContract.contractNumber
      const rooms = await this.props.repairsAndIquiriesStore.getPropertyRooms(contractNumber)
      this.rooms.replace(rooms)
      this.roomsLoadedForContract = contractNumber // Track which contract's rooms are loaded
      
      // Filter selectedRooms to only include rooms that still exist (remove deleted rooms)
      this.selectedRooms = this.selectedRooms.filter(selectedRoom => 
        rooms.some(room => room.id === selectedRoom.id)
      )
      
      // Update stored selections for this contract
      if (this.selectedContract && this.selectedRooms.length > 0) {
        this.roomSelectionsByContract[contractNumber] = [...this.selectedRooms]
      }
      
      // Announce rooms loaded for screen readers
      this.stepAnnouncement = `${t['SR_STEP_ROOM_LOADED'] || 'Rooms loaded'}: ${rooms.length} ${t['SR_STEP_ROOM_AVAILABLE'] || 'available'}`
    } catch (error) {
      this.roomsLoadError = extractErrorFromSap(error) || t[extractError(error)] || t['ERROR']
      // Still mark as loaded to prevent infinite loop, even on error
      this.roomsLoadedForContract = this.selectedContract.contractNumber
      // Error is already announced via Alert component with aria-live="assertive"
    } finally {
      this.loadingRooms = false
      // Clear announcement after a short delay
      setTimeout(() => {
        if (this.stepAnnouncement.includes('loaded') || this.stepAnnouncement.includes('Loading')) {
          this.stepAnnouncement = ''
        }
      }, 2000)
    }
  }


  nextStep = async () => {
    const contactDetailsEnabled = appFeatureEnabled('contactDetailsEnabled')
    const stepOffset = contactDetailsEnabled ? 1 : 0

    // From step 1 (contract): when contact details feature is on, go to step 2 (contact details step)
    if (this.currentStep === 1 && this.selectedContract && contactDetailsEnabled) {
      if (this.selectedContract.contractNumber) {
        this.loadingContractStep = true
        try {
          await this.props.contractStore.getContractDetails(this.selectedContract.contractNumber)
          try {
            this.contractType = await this.props.contractStore.getContractType(this.selectedContract.contractNumber)
          } catch (err) {
            // Non-blocking: allow user to continue even if contract type fetch fails
          }
        } finally {
          this.loadingContractStep = false
        }
      }
      this.currentStep += 1
      this.announceStepChange()
      return
    }

    // From step 1 (contract): when contact details feature is off, go to step 2 (request type)
    if (this.currentStep === 1 && this.selectedContract && !contactDetailsEnabled) {
      if (this.selectedContract.contractNumber) {
        this.loadingContractStep = true
        try {
          this.contractType = await this.props.contractStore.getContractType(this.selectedContract.contractNumber)
        } catch (err) {
          // Non-blocking: allow user to continue even if contract type fetch fails
        } finally {
          this.loadingContractStep = false
        }
      }
      this.currentStep += 1
      this.announceStepChange()
      return
    }

    // Show room step only when typeOfUsageMain === '0000' (Wohnraum)
    const showRoomStep = this.requestType === 'damage_report' && this.contractType?.typeOfUsageMain === '0000'
    const categoryStep = 3 + stepOffset
    const roomStep = 4 + stepOffset
    const urgencyStep = 5 + stepOffset

    // From category step (damage report): skip room step if typeOfUsageMain !== '0000'
    if (this.currentStep === categoryStep && this.requestType === 'damage_report') {
      if (!showRoomStep) {
        this.selectedRooms = []
      }
      this.currentStep += showRoomStep ? 1 : 2
      this.announceStepChange()
      return
    }

    // If leaving the rooms step (step 4+offset for damage report), filter selected rooms to only include existing rooms
    if (this.currentStep === roomStep && this.requestType === 'damage_report') {
      // Filter selectedRooms to only include rooms that exist in the current rooms array
      this.selectedRooms = this.selectedRooms.filter(selectedRoom => 
        this.rooms.some(room => room.id === selectedRoom.id)
      )
      
      // Also update the stored selections for this contract
      if (this.selectedContract) {
        this.roomSelectionsByContract[this.selectedContract.contractNumber] = [...this.selectedRooms]
      }
    }
    
    this.currentStep += 1
    this.announceStepChange()
  }

  announceStepChange = () => {
    const contactDetailsEnabled = appFeatureEnabled('contactDetailsEnabled')
    const stepTitles = [
      '',
      t['SR_STEP_CONTRACT_TITLE'],
      ...(contactDetailsEnabled ? [t['SR_STEP_TITLE_CONTACT_DETAILS'] || t.CONTACT_DETAILS_TITLE] : []),
      t['SR_STEP_REQUEST_TYPE_TITLE'] || 'Request Type',
      t['SR_STEP_GENERAL_REQUEST_TITLE'] || 'General Request',
      t['SR_STEP_CATEGORY_TITLE'],
      t['SR_STEP_ROOM_TITLE'],
      t['SR_STEP_URGENCY_TITLE'],
      t['SR_STEP_NEIGHBOR_TITLE'],
      t['SR_STEP_DESCRIPTION_TITLE'],
      t['SR_STEP_FILE_TITLE'],
      t['SR_STEP_REVIEW_TITLE']
    ]
    this.stepAnnouncement = `${t['SR_STEP_CHANGED'] || 'Step changed'} ${this.currentStep}: ${stepTitles[this.currentStep] || ''}`
    setTimeout(() => {
      this.stepAnnouncement = ''
    }, 1000)
  }

  prevStep = () => {
    const contactDetailsEnabled = appFeatureEnabled('contactDetailsEnabled')
    const stepOffset = contactDetailsEnabled ? 1 : 0
    const requestTypeStep = 2 + stepOffset
    const categoryStep = 3 + stepOffset
    const roomStep = 4 + stepOffset
    const urgencyStep = 5 + stepOffset

    // Handle special navigation logic based on request type
    if (this.currentStep === categoryStep && this.requestType === 'damage_report') {
      // Going back from category to request type
      this.currentStep = requestTypeStep
    } else if (this.currentStep === categoryStep && this.requestType === 'general') {
      // Going back from general request to request type
      this.currentStep = requestTypeStep
    } else if (this.currentStep === urgencyStep && this.requestType === 'damage_report' && !this.showRoomStep) {
      // Going back from urgency to category when room step is hidden
      this.currentStep = categoryStep
    } else {
      this.currentStep -= 1
    }

    // If going back to rooms step (step 4+offset for damage report), reset the room error so user can retry
    if (this.currentStep === roomStep && this.requestType === 'damage_report') {
      const hadError = !!this.roomsLoadError
      this.roomsLoadError = null
      // Reset the loaded contract marker to allow retry if there was an error
      if (hadError) {
        this.roomsLoadedForContract = null
      }
    }

    this.announceStepChange()
  }

  renderContactDetailsStep = () => {
    return (
      <ContactDetailsModal
        asStep
        isOpen={true}
        onConfirm={this.nextStep}
        onCancel={this.prevStep}
        source="new_sr"
      />
    )
  }

  renderContractSelection = () => {
    const { activeContracts } = this.props.contractStore

    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="contract-selection-title">{t['SR_STEP_CONTRACT_TITLE']}</h2>
          <p>{t['SR_STEP_CONTRACT_DESC']}</p>
        </div>
        
        <div className="contract-list" role="radiogroup" aria-labelledby="contract-selection-title">
          {activeContracts.map((contract) => (
            <div 
              key={contract.id}
              className={`contract-item ${contract.id === this.selectedContract?.id ? 'checked' : ''}`}
              onClick={() => this.handleContractSelect(contract)}
              onKeyDown={(e) => this.handleKeyDown(e, () => this.handleContractSelect(contract))}
              role="radio"
              aria-checked={contract.id === this.selectedContract?.id}
              aria-label={`${contract.contractNumber} - ${contract.address}`}
              tabIndex={0}
            >
              <div className="contract-info">
                <h3>{contract.contractNumber}</h3>
                <p>{contract.address}</p>
              </div>
              <div className="contract-checkbox">
                <div className={`checkbox ${contract.id === this.selectedContract?.id ? 'checked' : ''}`}>
                  <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12"></polyline>
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="contract-step-actions">
          <Link 
            to="/repairs-and-inquiries" 
            className="button secondary"
            onClick={() => historyService.push('/repairs-and-inquiries')}
            aria-label={t['SR_STEP_BUTTON_CANCEL']}
          >
            {t['SR_STEP_BUTTON_CANCEL']}
          </Link>
          <button 
            className={`button primary ${!this.selectedContract ? 'disabled' : ''}`}
            onClick={this.nextStep}
            disabled={!this.selectedContract}
            aria-label={t['SR_STEP_BUTTON_NEXT']}
            aria-disabled={!this.selectedContract}
          >
            {t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  renderRequestTypeSelection = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="request-type-selection-title">{t['SR_STEP_REQUEST_TYPE_TITLE'] || 'Request Type'}</h2>
          <p>{t['SR_STEP_REQUEST_TYPE_DESC'] || 'Please select the type of request you would like to submit.'}</p>
        </div>
        
        <div className="request-type-options" role="radiogroup" aria-labelledby="request-type-selection-title" style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div 
            className={`request-type-option ${this.requestType === 'general' ? 'checked' : ''}`}
            onClick={() => this.handleRequestTypeSelect('general')}
            onKeyDown={(e) => this.handleKeyDown(e, () => this.handleRequestTypeSelect('general'))}
            role="radio"
            aria-checked={this.requestType === 'general'}
            aria-label="General Request"
            tabIndex={0}
            style={{ 
              flex: '0 0 auto', 
              minWidth: '200px', 
              maxWidth: '300px',
              padding: '20px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: this.requestType === 'general' ? '#f5f5f5' : '#ffffff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div className="request-type-checkbox" style={{ flexShrink: 0, marginTop: '2px' }}>
                <div className={`checkbox ${this.requestType === 'general' ? 'checked' : ''}`}>
                  <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12"></polyline>
                  </svg>
                </div>
              </div>
              <div className="request-type-label" style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                  {t['SR_STEP_REQUEST_TYPE_GENERAL'] || 'General'}
                </div>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.4' }}>
                  {t['SR_STEP_REQUEST_TYPE_GENERAL_DESC'] || 'Submit a general inquiry or request'}
                </div>
              </div>
            </div>
          </div>
          <div 
            className={`request-type-option ${this.requestType === 'damage_report' ? 'checked' : ''}`}
            onClick={() => this.handleRequestTypeSelect('damage_report')}
            onKeyDown={(e) => this.handleKeyDown(e, () => this.handleRequestTypeSelect('damage_report'))}
            role="radio"
            aria-checked={this.requestType === 'damage_report'}
            aria-label="Damage Report"
            tabIndex={0}
            style={{ 
              flex: '0 0 auto', 
              minWidth: '200px', 
              maxWidth: '300px',
              padding: '20px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: this.requestType === 'damage_report' ? '#f5f5f5' : '#ffffff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div className="request-type-checkbox" style={{ flexShrink: 0, marginTop: '2px' }}>
                <div className={`checkbox ${this.requestType === 'damage_report' ? 'checked' : ''}`}>
                  <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12"></polyline>
                  </svg>
                </div>
              </div>
              <div className="request-type-label" style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                  {t['SR_STEP_REQUEST_TYPE_DAMAGE_REPORT'] || 'Damage Report'}
                </div>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.4' }}>
                  {t['SR_STEP_REQUEST_TYPE_DAMAGE_REPORT_DESC'] || 'Report damage or issues that need repair'}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="request-type-step-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className={`button primary ${!this.requestType ? 'disabled' : ''}`}
            onClick={this.nextStep}
            disabled={!this.requestType}
            aria-label={t['SR_STEP_BUTTON_NEXT']}
            aria-disabled={!this.requestType}
          >
            {t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  handleGeneralRequestSubjectChange = (event) => {
    this.subject = event.target.value
  }

  handleGeneralRequestDescriptionChange = (event) => {
    this.description = event.target.value
  }

  handleGeneralRequestUrgentChange = (event) => {
    const isUrgent = event.target.checked
    this.selectedUrgency = isUrgent
      ? this.urgencies.find(urg => urg.level === 'high') || this.urgencies[1]
      : this.urgencies.find(urg => urg.level === 'low') || this.urgencies[3]
  }

  addFilesGeneralRequest = async (newFiles) => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB in bytes
    const MAX_FILES = 10
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']

    const validFiles = newFiles.filter(file =>
      validTypes.includes(file.type) && file.size <= MAX_FILE_SIZE
    )
    const remainingSlots = Math.max(0, MAX_FILES - this.files.length)
    const filesToAdd = validFiles.slice(0, remainingSlots)

    for (const file of filesToAdd) {
      const processedFile = await this.resizeImage(file)
      const preview = await this.createPreview(processedFile)
      const fileId = Date.now() + Math.random()
      this.files.push({
        id: fileId,
        file: processedFile,
        name: file.name,
        size: processedFile.size,
        type: processedFile.type || file.type,
        preview,
        isExisting: false
      })
    }
  }

  handleGeneralRequestFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files || [])
    await this.addFilesGeneralRequest(selectedFiles)
    if (event.target) event.target.value = ''
  }

  handleGeneralRequestFileDrop = async (event) => {
    event.preventDefault()
    await this.addFilesGeneralRequest(Array.from(event.dataTransfer.files || []))
  }

  handleGeneralRequestDragOver = (event) => {
    event.preventDefault()
  }

  renderGeneralRequest = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="general-request-title">{t['SR_STEP_GENERAL_REQUEST_TITLE'] || 'General Request'}</h2>
          <p>{t['SR_STEP_GENERAL_REQUEST_DESC'] || 'Please fill in the details of your general request.'}</p>
        </div>
        
        <Form>
          {this.error && (
            <Alert 
              aria-live="assertive" 
              role='alert' 
              message={this.error} 
              type='error' 
              style={{marginTop: '12px', marginBottom: '12px'}} 
            />
          )}

          <Input
            name="subject"
            value={this.subject}
            label={t['DAMAGE_REPORT_SUBJECT'] || 'Subject'}
            required={true}
            onChange={this.handleGeneralRequestSubjectChange}
            aria-label={t['DAMAGE_REPORT_SUBJECT'] || 'Subject'}
          />
          
          <Textarea
            name="description"
            value={this.description}
            label={t['DAMAGE_REPORT_DESCRIPTION'] || 'Description'}
            required={true}
            placeholder={t['DAMAGE_REPORT_DESCRIPTION_PLACEHOLDER'] || 'Please describe your request...'}
            onChange={this.handleGeneralRequestDescriptionChange}
            aria-label={t['DAMAGE_REPORT_DESCRIPTION'] || 'Description'}
          />

          <div className="file-upload-container file-upload-container--general">
            <div
              className={`file-upload-area ${this.files.length >= 10 ? 'disabled' : ''}`}
              onDrop={this.files.length >= 10 ? undefined : this.handleGeneralRequestFileDrop}
              onDragOver={this.files.length >= 10 ? undefined : this.handleGeneralRequestDragOver}
              role="button"
              aria-label={t['SR_STEP_FILE_DRAG_AND_DROP_FILES']}
              aria-disabled={this.files.length >= 10}
              tabIndex={this.files.length >= 10 ? -1 : 0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && this.files.length < 10) {
                  e.preventDefault()
                  document.getElementById('file-input-general')?.click()
                }
              }}
            >
              <div className="upload-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <h3>{t['SR_STEP_FILE_DRAG_AND_DROP_FILES']}</h3>
              <p>{t['SR_STEP_FILE_OR']}</p>
              <label className={`upload-button ${this.files.length >= 10 ? 'disabled' : ''}`} htmlFor="file-input-general">
                <input
                  id="file-input-general"
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={this.handleGeneralRequestFileSelect}
                  disabled={this.files.length >= 10}
                  style={{ display: 'none' }}
                  aria-label={t['SR_STEP_FILE_BROWSE_FILES']}
                />
                {t['SR_STEP_FILE_BROWSE_FILES']}
              </label>
              <p className="upload-hint">{t['SR_STEP_FILE_MEDIA_SUPPORT']}</p>
            </div>

            {this.files.length > 0 && (
              <div className="file-previews" role="region" aria-label={t['SR_STEP_FILE_FILES']}>
                <h4>{t['SR_STEP_FILE_FILES']} ({this.files.length})</h4>
                <div className="preview-grid" role="list">
                  {this.files.map((fileItem) => (
                    <div key={fileItem.id} className="file-preview" role="listitem">
                      <div className="preview-content">
                        {fileItem.type && fileItem.type.startsWith('image/') ? (
                          <>
                            <img
                              src={fileItem.preview || fileItem.url}
                              alt={fileItem.name}
                              onError={(e) => {
                                e.target.style.display = 'none'
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'block'
                              }}
                            />
                            <div className="image-error" style={{ display: 'none' }}>
                              <div className="file-icon">📷</div>
                              <span>{t['SR_STEP_FILE_IMAGE_NOT_AVAILABLE']}</span>
                            </div>
                          </>
                        ) : (
                          <div className="pdf-preview">
                            <img src={pdfIcon} alt="PDF" />
                          </div>
                        )}
                      </div>
                      <div className="file-info">
                        <span className="file-name">{fileItem.name}</span>
                        <span className="file-size">{this.formatFileSize(fileItem.size)}</span>
                      </div>
                      <button
                        className="remove-file"
                        onClick={() => this.removeFile(fileItem.id)}
                        aria-label={`${t['SR_STEP_FILE_DELETE_FILE'] || 'Delete file'}: ${fileItem.name}`}
                        type="button"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Checkbox
            label={t['DAMAGE_REPORT_URGENT'] || 'Urgent'}
            value={this.selectedUrgency?.level === 'high' || this.selectedUrgency?.level === 'emergency'}
            onChange={this.handleGeneralRequestUrgentChange}
            aria-label={t['DAMAGE_REPORT_URGENT_LABEL'] || 'Urgent'}
          />
        </Form>
        
        <div className="general-request-step-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className={`button primary ${!this.subject || !this.description ? 'disabled' : ''}`}
            onClick={this.handleSubmit}
            disabled={!this.subject || !this.description}
            aria-label={t['SR_STEP_BUTTON_SUBMIT'] || t['SR_STEP_BUTTON_NEXT']}
            aria-disabled={!this.subject || !this.description}
          >
            {t['SR_STEP_BUTTON_SUBMIT'] || t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  renderCategorySelection = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="category-selection-title">{t['SR_STEP_CATEGORY_TITLE']}</h2>
          <p>{t['SR_STEP_CATEGORY_DESC']}</p>
        </div>
        
        <div className="category-grid" role="radiogroup" aria-labelledby="category-selection-title">
          {this.categories.map((category) => (
            <div 
              key={category.id}
              className={`category-item ${category.id === this.selectedCategory?.id ? 'checked' : ''}`}
              onClick={() => this.handleCategorySelect(category)}
              onKeyDown={(e) => this.handleKeyDown(e, () => this.handleCategorySelect(category))}
              role="radio"
              aria-checked={category.id === this.selectedCategory?.id}
              aria-label={`${category.name} - ${category.description}`}
              tabIndex={0}
            >
              <div className={`category-icon ${category.id === 3 ? 'category-icon-outline' : ''}`}>
                <img src={category.icon} alt="" className="category-icon-img" aria-hidden="true" />
              </div>
              <div className="category-text">
                <h3>{category.name}</h3>
                <p>{category.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="category-step-actions">
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className={`button primary ${!this.selectedCategory ? 'disabled' : ''}`}
            onClick={this.nextStep}
            disabled={!this.selectedCategory}
            aria-label={t['SR_STEP_BUTTON_NEXT']}
            aria-disabled={!this.selectedCategory}
          >
            {t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  renderRoomSelection = () => {
    // Only load rooms if we haven't loaded them for this contract yet
    if (!this.loadingRooms && this.selectedContract && this.roomsLoadedForContract !== this.selectedContract.contractNumber) {
      this.loadRooms()
    }

    const isNextDisabled = this?.loadingRooms || this?.rooms && this?.rooms?.length > 0 && this?.selectedRooms?.length === 0

    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="room-selection-title">{t['SR_STEP_ROOM_TITLE']}</h2>
          <p>{t['SR_STEP_ROOM_DESC']}</p>
        </div>
        
        {this.loadingRooms ? (
          <div className="loading-overlay" role="status" aria-live="polite" aria-atomic="true">
            <div className="loading-spinner"></div>
            <p>{t['SR_STEP_ROOM_LOADING_ROOMS']}...</p>
          </div>
        ) : (
          <>
            {this.roomsLoadError && (
              <Alert 
                message={this.roomsLoadError} 
                type="error" 
                style={{marginTop: '12px', marginBottom: '12px'}} 
                aria-live="assertive" 
                role="alert" 
              />
            )}
            {this.rooms.length === 0 ? (
              <div style={{textAlign: 'center', padding: '40px 20px'}}>
                <p style={{fontSize: '16px', color: '#666'}}>{t['SR_STEP_ROOM_NO_AVAILABLE_ROOMS']}</p>
              </div>
            ) : (
              <div className="room-list" role="group" aria-labelledby="room-selection-title">
                {this.rooms.map((room) => (
                <div 
                  key={room.id}
                  className={`room-item ${this.selectedRooms.some(r => r.id === room.id) ? 'checked' : ''}`}
                  onClick={() => this.handleRoomSelect(room)}
                  onKeyDown={(e) => this.handleKeyDown(e, () => this.handleRoomSelect(room))}
                  role="checkbox"
                  aria-checked={this.selectedRooms.some(r => r.id === room.id)}
                  aria-label={`${localStorage.getItem('locale') === 'en' ? room.name : room.nameDe} - ${room.description}`}
                  tabIndex={0}
                >
                  <div className="room-info">
                    <h3>{localStorage.getItem('locale') === 'en' ? room.name : room.nameDe}</h3>
                    <p>{localStorage.getItem('locale') === 'en' ? room.description : room.descriptionDe}</p>
                  </div>
                  <div className="room-checkbox">
                    <div className={`checkbox ${this.selectedRooms.some(r => r.id === room.id) ? 'checked' : ''}`}>
                      <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20,6 9,17 4,12"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                ))}
              </div>
            )}
          </>
        )}
        
        <div className="room-step-actions">
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className={`button primary ${isNextDisabled ? 'disabled' : ''}`}
            onClick={this.nextStep}
            disabled={isNextDisabled}
            aria-label={t['SR_STEP_BUTTON_NEXT']}
            aria-disabled={isNextDisabled}
          >
            {t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  renderUrgencySelection = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="urgency-selection-title">{t['SR_STEP_URGENCY_TITLE']}</h2>
          <p>{t['SR_STEP_URGENCY_DESC']}</p>
        </div>
        
        <div className="urgency-list" role="radiogroup" aria-labelledby="urgency-selection-title">
          {this.urgencies.map((urgency) => (
            <div 
              key={urgency.id}
              className={`urgency-item ${urgency.id === this.selectedUrgency?.id ? 'checked' : ''}`}
              onClick={() => this.handleUrgencySelect(urgency)}
              onKeyDown={(e) => this.handleKeyDown(e, () => this.handleUrgencySelect(urgency))}
              role="radio"
              aria-checked={urgency.id === this.selectedUrgency?.id}
              aria-label={`${urgency.name} - ${urgency.description}`}
              tabIndex={0}
            >
              <div className="urgency-indicator" style={{ backgroundColor: urgency.color }}></div>
              <div className="urgency-info">
                <h3>{urgency.name}</h3>
                <p>{urgency.description}</p>
              </div>
              <div className="urgency-checkbox">
                <div className={`checkbox ${urgency.id === this.selectedUrgency?.id ? 'checked' : ''}`}>
                  <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12"></polyline>
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="urgency-step-actions">
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className={`button primary ${!this.selectedUrgency ? 'disabled' : ''}`}
            onClick={this.nextStep}
            disabled={!this.selectedUrgency}
            aria-label={t['SR_STEP_BUTTON_NEXT']}
            aria-disabled={!this.selectedUrgency}
          >
            {t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  renderNeighborImpactSelection = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="neighbor-selection-title">{t['SR_STEP_NEIGHBOR_TITLE']}</h2>
          <p>{t['SR_STEP_NEIGHBOR_DESC']}</p>
        </div>
        
        <div className="neighbor-options" role="radiogroup" aria-labelledby="neighbor-selection-title">
          {this.neighborOptions.map((option) => (
            <div 
              key={option.id}
              className={`neighbor-option ${this.affectsNeighbors === option.value ? 'checked' : ''}`}
              onClick={() => this.handleNeighborSelect(option)}
              onKeyDown={(e) => this.handleKeyDown(e, () => this.handleNeighborSelect(option))}
              role="radio"
              aria-checked={this.affectsNeighbors === option.value}
              aria-label={option.label}
              tabIndex={0}
            >
              <div className="neighbor-label">
                <h3>{option.label}</h3>
              </div>
            </div>
          ))}
        </div>
        
        <div className="neighbor-step-actions">
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className={`button primary ${this.affectsNeighbors === null ? 'disabled' : ''}`}
            onClick={this.nextStep}
            disabled={this.affectsNeighbors === null}
            aria-label={t['SR_STEP_BUTTON_NEXT']}
            aria-disabled={this.affectsNeighbors === null}
          >
            {t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  renderDescriptionInput = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="description-input-title">{t['SR_STEP_DESCRIPTION_TITLE']}</h2>
          <p>{t['SR_STEP_DESCRIPTION_DESC']}</p>
        </div>
        
        <div className="description-input">
          <label htmlFor="description-textarea" className="sr-only">{t['SR_STEP_DESCRIPTION_INPUT_DESCRIPTION_PLACEHOLDER']}</label>
          <textarea
            id="description-textarea"
            className="description-textarea"
            placeholder={t['SR_STEP_DESCRIPTION_INPUT_DESCRIPTION_PLACEHOLDER']}
            value={this.description}
            onChange={this.handleDescriptionChange}
            rows={6}
            maxLength={500}
            aria-label={t['SR_STEP_DESCRIPTION_INPUT_DESCRIPTION_PLACEHOLDER']}
            aria-describedby="character-count"
          />
          <div id="character-count" className="character-count" aria-live="polite">
            {this.description.length}/500 {t['SR_STEP_DESCRIPTION_NUM_OF_CHARACTERS']}
          </div>
        </div>
        
        <div className="description-step-actions">
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className="button primary"
            onClick={this.nextStep}
            aria-label={t['SR_STEP_BUTTON_NEXT']}
          >
            {t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  renderFileUpload = () => {
    // Check if category or description changed since last analysis
    // Only check if we have previous analysis values (not null/undefined)
    const hasPreviousAnalysis = this.lastAnalysisCategory !== null || this.lastAnalysisDescription !== null
    const categoryChanged = hasPreviousAnalysis && this.lastAnalysisCategory !== (this.selectedCategory?.name || '')
    const descriptionChanged = hasPreviousAnalysis && this.lastAnalysisDescription !== (this.description || '')
    const needsReanalysis = categoryChanged || descriptionChanged
    
    // If category or description changed, re-analyze all images
    if (needsReanalysis && this.files.length > 0) {
      const imageFiles = this.files.filter(fileItem =>
        fileItem.type.startsWith('image/') &&
        fileItem.imageReference &&
        !this.analyzingImages.has(fileItem.id) // Don't re-analyze if already analyzing
      )
      
      if (imageFiles.length > 0) {
        // Re-analyze all images with new category/description
        imageFiles.forEach(async (fileItem) => {
          let fileToAnalyze = fileItem.file
          
          // For existing files, convert base64 to File if needed
          if (!fileToAnalyze && fileItem.base64Data && fileItem.isExisting) {
            try {
              const byteCharacters = atob(fileItem.base64Data)
              const byteNumbers = new Array(byteCharacters.length)
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
              }
              const byteArray = new Uint8Array(byteNumbers)
              const blob = new Blob([byteArray], { type: fileItem.type })
              fileToAnalyze = new File([blob], fileItem.name, { type: fileItem.type })
              fileItem.file = fileToAnalyze
            } catch (error) {
              console.warn('Failed to convert base64 to File for analysis:', error)
              return
            }
          }
          
          if (fileToAnalyze) {
            await this.analyzeImage(fileItem.id, fileToAnalyze, fileItem.imageReference)
          }
        })
        
        // Mark as analyzed to prevent duplicate analysis in edit mode
        this.hasAnalyzedOnFileStep = true
      }
    }
    
    // In edit mode, analyze all existing images when first landing on file step (only if not re-analyzing)
    if (this.isEditMode && !this.hasAnalyzedOnFileStep && this.files.length > 0 && !needsReanalysis) {
      // Find all image files that haven't been analyzed yet
      const imageFilesToAnalyze = this.files.filter(fileItem =>
        fileItem.type.startsWith('image/') &&
        fileItem.imageReference &&
        !this.imageAnalysisResults[fileItem.id] // Only analyze if not already analyzed
      )
      
      // Analyze each image
      if (imageFilesToAnalyze.length > 0) {
        this.hasAnalyzedOnFileStep = true
        imageFilesToAnalyze.forEach(async (fileItem) => {
          // For existing files, we need to convert base64 to File if needed
          let fileToAnalyze = fileItem.file
          
          if (!fileToAnalyze && fileItem.base64Data && fileItem.isExisting) {
            // Convert base64 to File object
            try {
              const byteCharacters = atob(fileItem.base64Data)
              const byteNumbers = new Array(byteCharacters.length)
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
              }
              const byteArray = new Uint8Array(byteNumbers)
              const blob = new Blob([byteArray], { type: fileItem.type })
              fileToAnalyze = new File([blob], fileItem.name, { type: fileItem.type })
              // Store it for future use
              fileItem.file = fileToAnalyze
            } catch (error) {
              console.warn('Failed to convert base64 to File for analysis:', error)
              return
            }
          }
          
          if (fileToAnalyze) {
            await this.analyzeImage(fileItem.id, fileToAnalyze, fileItem.imageReference)
          }
        })
      } else {
        // Mark as analyzed even if no images to analyze
        this.hasAnalyzedOnFileStep = true
      }
    }

    // Image files uploaded in general request (no imageReference): assign reference and analyze so they are analyzed when user switched to damage report
    const generalRequestImages = this.files.filter(fileItem =>
      fileItem.type && fileItem.type.startsWith('image/') &&
      !fileItem.imageReference &&
      fileItem.file &&
      !this.analyzingImages.has(fileItem.id)
    )
    if (generalRequestImages.length > 0) {
      generalRequestImages.forEach((fileItem) => {
        const imageReference = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        fileItem.imageReference = imageReference
        this.analyzeImage(fileItem.id, fileItem.file, imageReference)
      })
    }
    
    return (
      <div className="step-container">
        <div className="step-header">
          <h2 id="file-upload-title">{t['SR_STEP_FILE_TITLE']}</h2>
          <p>{t['SR_STEP_FILE_DESC']}</p>
        </div>
        
        <div className="file-upload-container">
          <div 
            className={`file-upload-area ${this.files.length >= 10 ? 'disabled' : ''}`}
            onDrop={this.files.length >= 10 ? undefined : this.handleFileDrop}
            onDragOver={this.files.length >= 10 ? undefined : this.handleDragOver}
            role="button"
            aria-label={t['SR_STEP_FILE_DRAG_AND_DROP_FILES']}
            aria-disabled={this.files.length >= 10}
            tabIndex={this.files.length >= 10 ? -1 : 0}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && this.files.length < 10) {
                e.preventDefault()
                document.getElementById('file-input').click()
              }
            }}
          >
            <div className="upload-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7,10 12,15 17,10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </div>
            <h3>{t['SR_STEP_FILE_DRAG_AND_DROP_FILES']}</h3>
            <p>{t['SR_STEP_FILE_OR']}</p>
            <label className={`upload-button ${this.files.length >= 10 ? 'disabled' : ''}`} htmlFor="file-input">
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={this.handleFileSelect}
                    disabled={this.files.length >= 10}
                    style={{ display: 'none' }}
                    aria-label={t['SR_STEP_FILE_BROWSE_FILES']}
                  />
              {t['SR_STEP_FILE_BROWSE_FILES']}
            </label>
                <p className="upload-hint">{t['SR_STEP_FILE_MEDIA_SUPPORT']}</p>
          </div>
          
          {this.files.length > 0 && (
            <div className="file-previews" role="region" aria-labelledby="file-upload-title">
              <h4>{t['SR_STEP_FILE_FILES']} ({this.files.length})</h4>
              <div className="preview-grid" role="list">
                {this.files.map((fileItem) => (
                  <div key={fileItem.id} className={`file-preview ${fileItem.isExisting ? 'existing-file' : ''}`} role="listitem">
                    <div className="preview-content">
                      {fileItem.type.startsWith('image/') ? (
                        <>
                          <img 
                            src={fileItem.preview || fileItem.url} 
                            alt={fileItem.name}
                            onError={(e) => {
                              e.target.style.display = 'none'
                              e.target.nextSibling.style.display = 'block'
                            }}
                          />
                          <div className="image-error" style={{display: 'none'}}>
                            <div className="file-icon">📷</div>
                            <span>{t['SR_STEP_FILE_IMAGE_NOT_AVAILABLE']}</span>
                          </div>
                          {/* Analysis loading indicator */}
                          {this.analyzingImages.has(fileItem.id) && (
                            <div className="analysis-loading">
                              <div className="loading-spinner"></div>
                              <span>{t['SR_STEP_FILE_ANALYZING']}...</span>
                            </div>
                          )}
                          {/* Warning icon indicator - only show when match is false */}
                          {this.imageAnalysisResults[fileItem.id] && 
                           !this.analyzingImages.has(fileItem.id) && 
                           !this.imageAnalysisResults[fileItem.id].match && (
                            <div 
                              className="analysis-warning-indicator"
                              onMouseEnter={() => this.showTooltipFor = fileItem.id}
                              onMouseLeave={() => this.showTooltipFor = null}
                              onClick={(e) => {
                                e.stopPropagation()
                                this.showTooltipFor = this.showTooltipFor === fileItem.id ? null : fileItem.id
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  this.showTooltipFor = this.showTooltipFor === fileItem.id ? null : fileItem.id
                                }
                              }}
                              role="button"
                              aria-label={`${t['SR_STEP_FILE_ANALYSIS_WARNING'] || 'Analysis warning'}: ${this.imageAnalysisResults[fileItem.id].reason}`}
                              tabIndex={0}
                            >
                              <WarningIcon width={16} height={16} color="#ffffff" />
                              {/* Tooltip - shows on hover or click */}
                              {this.showTooltipFor === fileItem.id && (
                                <div className="analysis-tooltip">
                                  <span>{this.imageAnalysisResults[fileItem?.id]?.reason || t['SR_STEP_FILE_ANALYSIS_FAILED_DEFAULT_REASON']}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="pdf-preview">
                          <img src={pdfIcon} alt="PDF" />
                        </div>
                      )}
                    </div>
                    <div className="file-info">
                      <span className="file-name">{fileItem.name}</span>
                      <span className="file-size">{this.formatFileSize(fileItem.size)}</span>
                    </div>
                    <button 
                      className="remove-file"
                      onClick={() => this.removeFile(fileItem.id)}
                      aria-label={`${t['SR_STEP_FILE_DELETE_FILE'] || 'Delete file'}: ${fileItem.name}`}
                      type="button"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="file-upload-step-actions">
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className={`button primary ${this.analyzingImages.size > 0 ? 'disabled' : ''}`}
            onClick={this.nextStep}
            disabled={this.analyzingImages.size > 0}
            aria-label={t['SR_STEP_BUTTON_NEXT']}
            aria-disabled={this.analyzingImages.size > 0}
          >
            {t['SR_STEP_BUTTON_NEXT']}
          </button>
        </div>
      </div>
    )
  }

  renderPreviewAndSubmit = () => {
    return (
      <div className="step-container">
        <div className="step-header">
          <h2>{t['SR_STEP_REVIEW_TITLE']}</h2>
          <p>{t['SR_STEP_REVIEW_DESC']}</p>
        </div>
        
        <div className="preview-summary">
          <div className="preview-section">
            <h3>{t['SR_STEP_REVIEW_CONTRACT']}</h3>
            <p>{this.selectedContract?.contractNumber} - {this.selectedContract?.address}</p>
          </div>
          
          <div className="preview-section">
            <h3>{t['SR_STEP_REVIEW_CATEGORY']}</h3>
            <p>{this.selectedCategory?.name} - {this.selectedCategory?.description}</p>
          </div>
          
          {this.showRoomStep && (
            <div className="preview-section">
              <h3>{t['SR_STEP_REVIEW_ROOMS']} ({this.selectedRooms.length})</h3>
              <div className="rooms-list">
                {this.selectedRooms.map((room, index) => (
                  <div key={room.id} className="room-item-preview">
                    <span className="room-name">{localStorage.getItem('locale') === 'en' ? room.name : room.nameDe}</span>
                    <span className="room-description">{room.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="preview-section">
            <h3>{t['SR_STEP_REVIEW_URGENCY']}</h3>
            <p>{this.selectedUrgency?.name} - {this.selectedUrgency?.description}</p>
          </div>
          
          <div className="preview-section">
            <h3>{t['SR_STEP_REVIEW_AFFECTS_NEIGHBORS']}</h3>
            <p>{this.affectsNeighbors ? t['SR_STEP_NEIGHBOR_OPTION_YES'] : t['SR_STEP_NEIGHBOR_OPTION_NO']}</p>
          </div>
          
          {this.description && (
            <div className="preview-section">
              <h3>{t['SR_STEP_REVIEW_DESCRIPTION']}</h3>
              <p>{this.description}</p>
            </div>
          )}
          
          {this.files.length > 0 && (
            <div className="preview-section">
              <h3>{t['SR_STEP_REVIEW_ATTACHMENTS']} ({this.files.length})</h3>
              <div className="preview-files-grid">
                {this.files.map((fileItem) => (
                  <div key={fileItem.id} className="preview-file-card">
                    <div className="preview-file-content">
                      {fileItem.type.startsWith('image/') ? (
                        <img src={fileItem.preview} alt={fileItem.name} />
                      ) : (
                        <div className="pdf-preview">
                          <img src={pdfIcon} alt="PDF" />
                        </div>
                      )}
                    </div>
                    <div className="preview-file-info">
                      <span className="file-name">{fileItem.name}</span>
                      <span className="file-size">{this.formatFileSize(fileItem.size)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="preview-step-actions">
          <button 
            className="button secondary"
            onClick={this.prevStep}
            aria-label={t['SR_STEP_BUTTON_BACK']}
          >
            {t['SR_STEP_BUTTON_BACK']}
          </button>
          <button 
            className="button primary submit-button"
            onClick={this.handleSubmit}
            disabled={this.submitting}
            aria-label={t['SR_STEP_BUTTON_SUBMIT']}
            aria-disabled={this.submitting}
          >
            {t['SR_STEP_BUTTON_SUBMIT']}
          </button>
        </div>
        
        {this.error && (
          <Alert 
            message={this.error} 
            type="error" 
            style={{marginTop: '12px', marginBottom: '12px'}} 
            aria-live="assertive" 
            role="alert" 
          />
        )}
      </div>
    )
  }

  renderStepContent = () => {
    const contactDetailsEnabled = appFeatureEnabled('contactDetailsEnabled')

    switch (this.currentStep) {
      case 1:
        return this.renderContractSelection()
      case 2:
        if (contactDetailsEnabled) {
          return this.renderContactDetailsStep()
        }
        return this.renderRequestTypeSelection()
      case 3:
        if (contactDetailsEnabled) {
          return this.renderRequestTypeSelection()
        }
        // Step 3: General request (blank) or Category (for damage report) when contact details off
        if (this.requestType === 'general') {
          return this.renderGeneralRequest()
        } else if (this.requestType === 'damage_report') {
          return this.renderCategorySelection()
        }
        return null
      case 4:
        if (contactDetailsEnabled) {
          if (this.requestType === 'general') {
            return this.renderGeneralRequest()
          } else if (this.requestType === 'damage_report') {
            return this.renderCategorySelection()
          }
          return null
        }
        // Step 4: Room (for damage report only)
        if (this.requestType === 'damage_report') {
          return this.renderRoomSelection()
        }
        return null
      case 5:
        if (contactDetailsEnabled) {
          if (this.requestType === 'damage_report') {
            return this.renderRoomSelection()
          }
          return null
        }
        // Step 5: Urgency (for damage report only)
        if (this.requestType === 'damage_report') {
          return this.renderUrgencySelection()
        }
        return null
      case 6:
        if (contactDetailsEnabled) {
          if (this.requestType === 'damage_report') {
            return this.renderUrgencySelection()
          }
          return null
        }
        // Step 6: Neighbor (for damage report only)
        if (this.requestType === 'damage_report') {
          return this.renderNeighborImpactSelection()
        }
        return null
      case 7:
        if (contactDetailsEnabled) {
          if (this.requestType === 'damage_report') {
            return this.renderNeighborImpactSelection()
          }
          return null
        }
        // Step 7: Description (for damage report only)
        if (this.requestType === 'damage_report') {
          return this.renderDescriptionInput()
        }
        return null
      case 8:
        if (contactDetailsEnabled) {
          if (this.requestType === 'damage_report') {
            return this.renderDescriptionInput()
          }
          return null
        }
        // Step 8: File upload (for damage report only)
        if (this.requestType === 'damage_report') {
          return this.renderFileUpload()
        }
        return null
      case 9:
        if (contactDetailsEnabled) {
          if (this.requestType === 'damage_report') {
            return this.renderFileUpload()
          }
          return null
        }
        // Step 9: Review and submit (for damage report only)
        if (this.requestType === 'damage_report') {
          return this.renderPreviewAndSubmit()
        }
        return null
      case 10:
        if (contactDetailsEnabled && this.requestType === 'damage_report') {
          return this.renderPreviewAndSubmit()
        }
        return null
      default:
        return null
    }
  }

  render() {
    const { user } = this.context

    // Show loading overlay while submitting
    if (this.submitting) {
      return <LoadingPageOverlay />
    }

    // Show loading overlay if fetching service request data
    if (this.loadingServiceRequest) {
      return <LoadingPageOverlay />
    }

    // Show loading overlay when fetching contract details/type
    if (this.loadingContractStep) {
      return <LoadingPageOverlay />
    }

    // Show thank you overlay if submission was successful
    if (this.showThankYou) {
      return <ThankYouOverlay
        title={t.THANK_YOU}
        description={t.DAMAGE_REPORT_SUCCESS}
        buttonText={t.BACK_TO_REPAIRS_AND_INQUIRES}
        buttonLink='/repairs-and-inquiries'
      />
    }

    return (
      <div className="container white report repairs-new-request-v2" role="region" aria-labelledby="report-section">
        {/* Screen reader announcements */}
        <div 
          className="sr-only" 
          aria-live="polite" 
          aria-atomic="true"
          role="status"
        >
          {this.stepAnnouncement}
        </div>
        <div 
          className="sr-only" 
          aria-live="assertive" 
          aria-atomic="true"
          role="alert"
        >
          {this.fileActionAnnouncement}
        </div>
        
        <div className="step-form">
        <div className={`step-indicator ${this.requestType === 'damage_report' ? 'step-indicator--two-rows' : ''}`}>
          {(() => {
            const contactDetailsEnabled = appFeatureEnabled('contactDetailsEnabled')
            const stepOffset = contactDetailsEnabled ? 1 : 0
            return (
              <>
                <div className={`step ${this.currentStep >= 1 ? 'active' : ''}`}>
                  <span className="step-number">1</span>
                  <span className="step-label">{t['SR_STEP_TITLE_CONTRACT']}</span>
                </div>
                {contactDetailsEnabled && (
                  <div className={`step ${this.currentStep >= 2 ? 'active' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-label">{t['SR_STEP_TITLE_CONTACT_DETAILS'] || t.CONTACT_DETAILS_TITLE}</span>
                  </div>
                )}
                <div className={`step ${this.currentStep >= 2 + stepOffset ? 'active' : ''}`}>
                  <span className="step-number">{2 + stepOffset}</span>
                  <span className="step-label">{t['SR_STEP_TITLE_REQUEST_TYPE'] || 'Request Type'}</span>
                </div>
                {this.requestType === 'general' ? (
                  <div className={`step ${this.currentStep >= 3 + stepOffset ? 'active' : ''}`}>
                    <span className="step-number">{3 + stepOffset}</span>
                    <span className="step-label">{t['SR_STEP_TITLE_GENERAL_REQUEST'] || 'General Request'}</span>
                  </div>
                ) : this.requestType === 'damage_report' ? (
                  <>
                    <div className={`step ${this.currentStep >= 3 + stepOffset ? 'active' : ''}`}>
                      <span className="step-number">{3 + stepOffset}</span>
                      <span className="step-label">{t['SR_STEP_TITLE_CATEGORY']}</span>
                    </div>
                    {this.showRoomStep && (
                      <div className={`step ${this.currentStep >= 4 + stepOffset ? 'active' : ''}`}>
                        <span className="step-number">{4 + stepOffset}</span>
                        <span className="step-label">{t['SR_STEP_TITLE_ROOM']}</span>
                      </div>
                    )}
                    <div className={`step ${this.currentStep >= 5 + stepOffset ? 'active' : ''}`}>
                      <span className="step-number">{5 + stepOffset}</span>
                      <span className="step-label">{t['SR_STEP_TITLE_URGENCY']}</span>
                    </div>
                    <div className={`step ${this.currentStep >= 6 + stepOffset ? 'active' : ''}`}>
                      <span className="step-number">{6 + stepOffset}</span>
                      <span className="step-label">{t['SR_STEP_TITLE_NEIGHBORS']}</span>
                    </div>
                    <div className={`step ${this.currentStep >= 7 + stepOffset ? 'active' : ''}`}>
                      <span className="step-number">{7 + stepOffset}</span>
                      <span className="step-label">{t['SR_STEP_TITLE_DESCRIPTION']}</span>
                    </div>
                    <div className={`step ${this.currentStep >= 8 + stepOffset ? 'active' : ''}`}>
                      <span className="step-number">{8 + stepOffset}</span>
                      <span className="step-label">{t['SR_STEP_TITLE_FILES']}</span>
                    </div>
                    <div className={`step ${this.currentStep >= 9 + stepOffset ? 'active' : ''}`}>
                      <span className="step-number">{9 + stepOffset}</span>
                      <span className="step-label">{t['SR_STEP_TITLE_REVIEW']}</span>
                    </div>
                  </>
                ) : null}
              </>
            )
          })()}
        </div>

          {this.renderStepContent()}

          <div className="step-actions">
            {/* No buttons needed here - each step has its own actions */}
          </div>
        </div>

      </div>
    )
  }
}

const RepairsAndInquiriesNewRequestV2 = withRouter(inject('contractStore', 'repairsAndIquiriesStore')(observer(RepairsAndInquiriesNewRequestV2Class)))

export default RepairsAndInquiriesNewRequestV2
